// The FR86 deadline wording that MORE THAN ONE surface shows (Story 20.1).
//
// Screen 38 panel 4a states the rule this module exists to make structural:
// "The same three labels are used on every surface — On track, Due soon, On hold
// — and all of them read from `deadline_state`. No surface re-derives its own
// wording." The Calibration Overview and the Profile "Access & readiness" card
// both render that label today, and Story 20.2 adds the hold treatment on top of
// it; three call sites each holding their own string is three strings that can
// drift, and the drift is silent — a clinician reads one word on Profile and a
// different one on the Overview for the same state.
//
// Everything here is pure and data-driven. The DECISION lives in the backend's
// single derivation (`calibration/_shared/deadline.py`); this module only names
// what the backend already decided.

import type { DeadlineState } from "@/hooks/queries/calibration-status-types";

/**
 * The states that put something on the screen. `not_applicable` is deliberately
 * excluded rather than mapped to an empty string: it is the EXEMPTION, and a
 * surface must render nothing deadline-shaped for it (AC6) — not a blank badge.
 * Keeping it out of the key set makes that a type error instead of a judgement.
 */
export type ShownDeadlineState = Exclude<DeadlineState, "not_applicable">;

/** The three shared labels, keyed on `deadline_state` and nothing else (AC10). */
export const DEADLINE_STATE_LABEL: Record<ShownDeadlineState, string> = {
  active: "On track",
  due_soon: "Due soon",
  held: "On hold",
};

/**
 * Badge variants for the three labels. NO ERROR TOKEN IN ANY STATE, `held`
 * included (DW-669, ruled 2026-09-15).
 *
 * `due_soon` gets `pending` and `held` gets `warning` — `badge.tsx` defines the
 * two as the SAME `--bg-warning-primary` / `--bg-warning-secondary` /
 * `--text-warning` triple, and they are named apart only so the call site reads
 * intent-first. `held` is not "waiting"; it is the muted warning.
 *
 * WHAT THIS OVERRULES, on purpose. Screen 38 draws the held pill as
 * `.pill.blocked` (`--danger-soft` / `--danger`, `amps-hifi-screens.css:272-275`)
 * at three sites, and 20.2's AC9 cites `Badge variant="blocked"` by line — but
 * the same AC's next sentence forbids every `--*-error-*` token on that surface,
 * and CLAUDE.md's non-negotiable is that status colours are deliberately
 * desaturated to avoid clinical alarm. The day-61 hold is the state that rule
 * exists for: the account, the data and the purchase are all retained and the
 * countdown has STOPPED, not failed. `info` was rejected as the opposite error —
 * it understates a state that blocks work. Screen 38 is in-house and
 * PM-accepted, NOT client-reviewed (its own doc-head :192), so the drawing does
 * not outrank the constraint; the mockup and both ACs were reconciled to this.
 *
 * The cost is that `due_soon` and `held` now carry the same colour. That is
 * admissible here and nowhere near colour-only signalling: the labels differ,
 * `badge.tsx` requires explicit text in every pill, and the Overview separates
 * the two states further with the hold banner, the "What to do next" section and
 * the inert countdown tile.
 *
 * `blocked` is untouched in `badge.tsx` — the ORG-lapsed pill still uses it.
 */
export const DEADLINE_STATE_BADGE_VARIANT: Record<
  ShownDeadlineState,
  "ok" | "pending" | "warning"
> = {
  active: "ok",
  due_soon: "pending",
  held: "warning",
};

/**
 * Panel 3's four guarantees, TRANSCRIBED from
 * `docs/screens/amps-screen-38-calibration-deadline.html` and not paraphrased
 * (Story 20.2 AC9, boundary 18). They are the client's own terms and FR86
 * restates them; softening one, merging two or adding a fifth is a change to
 * what CITS has promised a held clinician, not a copy edit.
 *
 * They live here rather than in the Overview screen because the Profile card's
 * held block (AC11) makes the same promise in shorter form, and AC12's whole
 * point is that neither surface authors its own hold wording.
 */
export const HOLD_BANNER_HEADING = "Calibration progression is on temporary hold.";

export const HOLD_GUARANTEES = [
  "You keep your account and all existing information — clients, evaluations and saved case scores are untouched.",
  "You are not eligible for live AMPS scoring while the hold is in place.",
  "Your purchase is not forfeited. Nothing has been cancelled and nothing has been refunded.",
  "A CITS super admin can review the circumstances and extend your deadline or reactivate your account.",
] as const;

/**
 * The `mailto:` route a held clinician is given instead of a dead end — the
 * single exported constant Story 20.2 boundary 8 chose over a backend setting.
 *
 * **The address is the AMPS Director role inbox, settled by the PM 2026-09-15.**
 * It replaces the mockup's demo placeholder `support@cits-demo.com`
 * (`amps-screen-38-calibration-deadline.html` panel 3 toolbar). The inbox is a
 * ROLE inbox, not a person's: the client supplied it 2026-05, confirmed it live
 * 2026-07-15, and org_admin seat requests already arrive there today. The named
 * tests pin the `mailto:` SHAPE and the subject line, never the address,
 * precisely so this edit moves no test.
 *
 * Not a backend setting: that would be a full W6 obligation and two SSM
 * parameters in two environments for a value nobody has asked to vary per
 * environment. The backend's own `AMPS_DIRECTOR_INBOX` setting holds the same
 * address for the mail the SERVER sends (org seat requests, B2B renewal
 * reminders), but it cannot reach this constant — this `mailto:` is opened by
 * the clinician's own mail client, so the value has to be compiled in.
 *
 * `DEADLINE_REVIEW_SUBJECT` is a stable contract, not decoration: it is the only
 * thing that lets the shared inbox tell a clinician's deadline-review request
 * apart from the organization traffic it already carries.
 */
export const DEADLINE_REVIEW_SUBJECT = "Calibration deadline review";

export const DEADLINE_REVIEW_EMAIL =
  "amps-director@centerforinnovativetherapysolutions.com";

export const DEADLINE_REVIEW_MAILTO = `mailto:${DEADLINE_REVIEW_EMAIL}?subject=${encodeURIComponent(
  DEADLINE_REVIEW_SUBJECT,
)}`;

export const DEADLINE_REVIEW_CTA_LABEL = "Request a deadline review";

/**
 * Why the case-study affordances are inert while held (AC10, D9).
 *
 * Rendered as VISIBLE TEXT beside each control, never as the native `title` the
 * mockup uses. `routes/calibration/case-access.ts` states the rule on this very
 * board: the iPad PWA is the primary clinical surface and `title` is mouse-only
 * there (the standing gh-555/gh-568 finding). The mockup's `title=` is recorded
 * as mockup hygiene in the W3 reconciliation, not followed.
 */
export const HOLD_INERT_REASON = "Paused while your calibration is on hold.";

/**
 * What the `Clock started` date IS, stated without claiming WHICH EVENT produced
 * it (DW-660).
 *
 * The cohort-keyed version of this label — "Certificate verified by CITS" for
 * `webapp_legacy`, "Account activated" for everyone else — is false in two
 * ways, and neither is detectable from this side of the wire:
 *
 * 1. `CALIBRATION_DEADLINE_ANCHOR_FLOOR`. The derivation REPLACES the anchor
 *    with UTC midnight of the floor date and sends that as `deadline_anchor_at`
 *    (`calibration/_shared/deadline.py`), so wherever a floor is in force the
 *    tile prints the FLOOR DATE under "Certificate verified by CITS". No
 *    environment carries one: the floor is set NOWHERE — not develop, not
 *    staging, not production, not local — and the only calibration-deadline
 *    parameter provisioned to SSM is `CALIBRATION_DEADLINE_DAYS = 60` (DW-671
 *    and DW-716, both resolved (cancelled) 2026-09-15). The reason is the hold,
 *    not this label: effective_anchor = max(anchor, floor), so a floor that IS
 *    set makes every clamped clinician derive `active` until floor +
 *    `CALIBRATION_DEADLINE_DAYS` — which puts the day-61 hold out of reach and
 *    makes every hold test pass VACUOUSLY. The setting stays supported and is
 *    still read on every request, so it remains a way this label can lie, just
 *    not one any deployment is exposed to today.
 * 2. The FR79 B2B population, which needs no floor at all. A `webapp_legacy`
 *    clinician with NO upload row anchors on `activated_at` (boundary 2, AC3) —
 *    `+e20legacynoupload` in the QA catalog is exactly this account — and read
 *    "Certificate verified by CITS" over their activation stamp in production.
 *
 * The origin is not a discriminator for either. Until the derivation says which
 * rule produced the anchor, the only honest sub-label is one that describes the
 * FIELD rather than its cause.
 *
 * THE CORRECT FIX, when it is scheduled: the derivation reports the rule it
 * used (`cert_verified` / `activated` / `platform_floor`) as a new
 * `deadline_anchor_source` field on both wires, and this constant becomes a
 * three-arm record keyed on it. That touches two published payloads and the
 * highest-collision module in the calibration slice, so it is deliberately not
 * bundled here — a label that names nothing is correct today, and a label that
 * names the wrong event is not.
 */
export const DEADLINE_ANCHOR_FOOT = "Start of the calibration window";

/**
 * `"42 days"` / `"1 day"` — the countdown quantity, pluralized once.
 *
 * The Overview says "<n> days left to complete calibration" and the Profile card
 * says "<n> days remaining"; the sentences differ, the PLURAL RULE does not. Two
 * copies of `n === 1 ? "day" : "days"` is the shape that ends with one surface
 * reading "1 days" after somebody edits the other.
 */
export function deadlineDaysLabel(days: number): string {
  return `${days} day${days === 1 ? "" : "s"}`;
}

const LEADING_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

function utcDayMillis(iso: string): number | null {
  const match = LEADING_DATE.exec(iso);
  if (!match) return null;
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/**
 * The UTC calendar day of `deadline_anchor_at`, as a bare `YYYY-MM-DD` string.
 *
 * The anchor is an INSTANT on the wire (it names a certificate approval or an
 * account activation) while the deadline is a bare date, and the backend derives
 * one from the other on the UTC day (boundary 20). Rendering the instant through
 * the reader's local zone would print the previous day for everyone west of UTC,
 * and the page would then carry two lines that do not add up: an anchor, a
 * window, and a deadline that is not their sum. Take the UTC day and hand the
 * date-only string to `formatDate`, which renders a day as the day it names.
 */
export function deadlineAnchorDay(anchorAt: string | null | undefined): string | null {
  if (!anchorAt) return null;
  const match = LEADING_DATE.exec(anchorAt);
  return match ? anchorAt.slice(0, 10) : null;
}

/**
 * How long the window actually is, from the two dates the wire already carries.
 *
 * The screen says "60 days from the start date", and 60 is the DEFAULT of
 * `CALIBRATION_DEADLINE_DAYS` — a backend setting the frontend is never told.
 * Hardcoding it would be a second copy of a configurable number, silently wrong
 * on any deployment that changes the window and wrong for every clinician
 * carrying a Story 20.3 extension (their deadline is not anchor + the default).
 * Subtracting the two dates the clinician is being shown is the one answer that
 * cannot disagree with the rest of the panel.
 *
 * `null` when either date is absent — an org_admin's read carries the state
 * without the dates (AC14), and there is no window to name from nothing.
 */
export function deadlineWindowDays(
  anchorAt: string | null | undefined,
  deadlineAt: string | null | undefined,
): number | null {
  if (!anchorAt || !deadlineAt) return null;
  const anchorDay = utcDayMillis(anchorAt);
  const deadlineDay = utcDayMillis(deadlineAt);
  if (anchorDay === null || deadlineDay === null) return null;
  return Math.round((deadlineDay - anchorDay) / 86_400_000);
}
